import {
  ArrowLeft,
  Stethoscope,
  Calendar,
  DollarSign,
  GraduationCap,
} from "lucide-react";
import { DoctorDto } from "../web-api-client";
import { Button } from "../components/ui/button";
import { useNavigate } from "react-router-dom";
import { getDoctorDefaultImg } from "../utils/defaultImage";
import { useLanguage } from "../i18n/LanguageContext";
import { BookingButton } from "../components/landing/BookingButton";

interface props {
  doctor: DoctorDto;
  handleDoctorBooked: (doctor: DoctorDto) => void;
}
const DoctorProfileDesktop = ({ doctor, handleDoctorBooked }: props) => {
  const title = doctor?.name ?? "";
  const navigate = useNavigate();
  const { t } = useLanguage();

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Background bg-gradient-to-br from-purple-50 via-blue-50 */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-teal-200 rounded-full opacity-20"></div>
        <div className="absolute top-0 -left-40 w-60 h-60 bg-blue-200 rounded-full opacity-20"></div>
        <div className="absolute bottom-20 right-20 w-40 h-40 bg-indigo-200 rounded-full opacity-20"></div>
      </div>

      <div className="relative z-10 ">
        <div className="container mx-auto px-4 py-4 max-w-6xl">
          <div className="flex gap-4 items-stretch">
            {/* Right Content - Doctor Image */}
            <div className="relative w-full flex-1 shrink-0">
              <div className="flex items-center mb-8">
                <button className="flex items-center z-50 text-gray-600 hover:text-gray-800 transition-colors">
                  <ArrowLeft className="w-5 h-5 mr-2" />
                  <span
                    className="font-medium text-base"
                    onClick={() => navigate(-1)}
                  >
                    {t("quayve")}
                  </span>
                </button>
              </div>
              {doctor?.processedUrl || doctor?.originalUrl ? (
                <div className="absolute inset-0 flex justify-center items-end h-full">
                  <img
                    src={doctor.processedUrl ?? doctor.originalUrl}
                    alt={doctor.name}
                    className="w-auto h-[105%] object-cover"
                  />
                </div>
              ) : (
                doctor && (
                  <div className="absolute inset-0 flex justify-center items-center h-full">
                    <img
                      src={getDoctorDefaultImg(doctor.gender)}
                      alt="Doctor"
                      className="w-auto h-[70%] object-cover"
                    />
                  </div>
                )
              )}
            </div>
            {/* Left Content */}
            <div className="space-y-8 flex-1 py-4">
              {/* Doctor Info */}
              <div className="space-y-6 pb-4">
                <div>
                  <p className="text-2xl font-bold text-gray-900 mb-4">
                    {title}
                  </p>

                  <div className="flex flex-wrap flex-col gap-4">
                    <div className="flex-1 flex items-center gap-4 ">
                      <div className="w-[40px] h-[40px] shadow-[0_0_15px_5px_rgba(34,197,94,0.5)] shadow-green-100 bg-white rounded-full flex items-center justify-center">
                        <GraduationCap className="w-5 h-5 text-green-500" />
                      </div>
                      <div className="flex-1 flex items-center gap-2 text-base">
                        <p className="text-gray-800">{t("chucdanh")}:</p>
                        {doctor?.title || doctor?.titleDescription ? (
                          <p>
                            {doctor.titleDescription ?? doctor.formattedTitle}
                          </p>
                        ) : (
                          <p className="text-base">Đang cập nhật</p>
                        )}
                      </div>
                    </div>

                    {doctor?.specialty?.trim() && (
                      <div className="flex items-stretch gap-4  rounded-xl w-fit ">
                        <div className="w-[40px] h-[40px] shadow-[0_0_15px_5px_rgba(34,197,94,0.5)] shadow-green-100 bg-white rounded-full flex items-center justify-center">
                          <Stethoscope className="w-5 h-5 text-green-500" />
                        </div>
                        <div className="flex-1 flex items-center gap-4 text-base">
                          <p className="text-gray-800">{t("chuyenkhoa")}:</p>
                          <span className="text-foreground line-clamp-1 text-base">
                            {doctor.specialty}
                          </span>
                        </div>
                      </div>
                    )}

                    <div className="flex items-stretch gap-4  rounded-xl w-fit ">
                      <div className="w-[40px] h-[40px] shadow-[0_0_15px_5px_rgba(34,197,94,0.5)] shadow-green-100 bg-white rounded-full flex items-center justify-center">
                        <Calendar className="w-5 h-5 text-green-500" />
                      </div>
                      <div className="flex-1 flex items-center gap-4 text-base">
                        <p className="text-gray-800">{t("lichkham")}:</p>
                        <span className="text-foreground line-clamp-1 text-base">
                          {doctor?.daysThisWeek &&
                          doctor.daysThisWeek.length > 0
                            ? doctor.daysThisWeek.join(", ")
                            : t("khongcolichkham")}
                        </span>
                      </div>
                    </div>

                    <div className="flex items-stretch gap-4  rounded-xl w-fit">
                      <div className="w-[40px] h-[40px] shadow-[0_0_15px_5px_rgba(34,197,94,0.5)] shadow-green-100 bg-white rounded-full flex items-center justify-center">
                        <DollarSign className="w-5 h-5 text-green-500" />
                      </div>
                      <div className="flex-1 flex items-center gap-4 text-base">
                        <p className="text-gray-800">{t("giakham")}</p>
                        <span className="text-base text-primary">
                          {(doctor?.consultationFee ?? 1600000).toLocaleString(
                            "vi-VN",
                          )}
                          đ
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex justify-start sm:flex-row gap-4">
                  <Button
                    disabled={doctor?.daysThisWeek?.length === 0}
                    onClick={() => handleDoctorBooked(doctor)}
                    className=" bg-[linear-gradient(135deg,#2f7d44_0%,#368f4e_52%,#7cc46a_100%)] text-base text-white px-12 py-6 rounded-full font-semibold hover:bg-purple-700 transition-all duration-300 transform hover:scale-105 shadow-lg"
                    style={{ height: "auto" }}
                  >
                    <Calendar />

                    <span>
                      {doctor?.daysThisWeek && doctor.daysThisWeek?.length > 0
                        ? t("datkhamngay")
                        : t("hetlichkham")}
                    </span>
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div
          className="absolute inset-0 z-20 pointer-events-none"
          style={{
            background: `url(/images/doctor-item-top-wave.svg) center bottom / 100% 100% no-repeat`,
          }}
        />
      </div>

      <div className="bg-white z-10 px-6 py-8 min-h-[60vh] relative -mt-[1px]">
        <div className="absolute top-20 -left-20 w-60 h-60 bg-green-200 rounded-full opacity-20"></div>
        <div className="absolute bottom-20 right-20 w-40 h-40 bg-indigo-200 rounded-full opacity-20"></div>

        <div className="w-full max-w-6xl mx-auto">
          {doctor?.summary?.trim() ? (
            <div className="mt-6">
              <p className="text-lg md:text-xl font-medium text-green-800">
                {t("thongtinchung")}
              </p>
              <div className="w-20 h-[0.1rem] mt-2 mb-4 bg-green-400"></div>

              <div className="space-y-1">
                {doctor.summary
                  .split(/\r?\n/)
                  .map((line) => line.trim())
                  .filter(Boolean)
                  .map((line, idx) => (
                    <div className="flex items-start gap-3 ml-1" key={idx}>
                      <div className="w-[4px] h-[4px] bg-green-500 rounded-full flex-shrink-0 mt-3"></div>
                      <span className="text-gray-700">{line}</span>
                    </div>
                  ))}
              </div>
            </div>
          ) : doctor?.description && doctor.description.length > 0 ? (
            doctor.description.map((gr, indx) => (
              <div className="mt-6" key={indx}>
                <p className="text-lg md:text-xl font-medium text-green-800">
                  {gr.title || t("thongtinchung")}
                </p>
                <div className="w-20 h-[0.1rem] mt-2 mb-4 bg-green-400"></div>

                <div className="space-y-1">
                  {gr.items && gr.items.length > 0 ? (
                    gr.items.map((item, idx) => (
                      <div className="flex items-start gap-3 ml-1" key={idx}>
                        <div className="w-[4px] h-[4px] bg-green-500 rounded-full flex-shrink-0 mt-3"></div>
                        <span className="text-gray-700">{item}</span>
                      </div>
                    ))
                  ) : (
                    <div className="flex items-start gap-3 ml-1">
                      <div className="w-[4px] h-[4px] bg-green-500 rounded-full flex-shrink-0 mt-3"></div>
                      <span className="text-gray-500">{t("dangcapnhat")}</span>
                    </div>
                  )}
                </div>
              </div>
            ))
          ) : (
            <div className="mt-6">
              <p className="text-lg md:text-xl font-medium text-green-800">
                {t("thongtinchung")}
              </p>
              <div className="w-20 h-[0.1rem] mt-2 mb-4 bg-green-400"></div>

              <div className="space-y-1">
                <div className="flex items-start gap-3 ml-1">
                  <div className="w-[4px] h-[4px] bg-green-500 rounded-full flex-shrink-0 mt-3"></div>
                  <span className="text-gray-500">{t("dangcapnhat")}</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DoctorProfileDesktop;
